import {
  ArrowLeft,
  ArrowLeftRight,
  Ban,
  Calendar,
  Check,
  CircleUserRound,
  CreditCard,
  Eye,
  EyeOff,
  ExternalLink,
  FileImage,
  Landmark,
  History,
  Ticket,
  Mail,
  MapPin,
  MessageSquareText,
  Pencil,
  Phone,
  RefreshCw,
  ShieldAlert,
  Snowflake,
  Upload,
  X,
} from 'lucide-react';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { Loading } from '../components/Loading';
import { Pagination } from '../components/Pagination';
import { StatusBadge } from '../components/StatusBadge';
import { api, ApiError, dateTime, money } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Link, useLocation, useNavigate } from '../lib/router';
import type {
  Card,
  CustomerContact,
  CustomerContactPage,
  CustomerDetail,
  Document,
  Transaction,
  TransactionPage,
  UpdateCustomerInput,
  VoucherPurchase,
  VoucherPurchasePage,
} from '../lib/types';

type Decision = 'APPROVED' | 'REJECTED' | 'MORE_INFORMATION_REQUIRED';
type DecisionTarget = { decision: Decision; documentId?: string };
type DetailTab = 'overview' | 'documents' | 'decisions' | 'movements' | 'vouchers' | 'contacts';

const documentKindLabels: Record<string, string> = {
  ID_FRONT: 'Cartão de cidadão — frente',
  ID_BACK: 'Cartão de cidadão — verso',
  SELFIE: 'Fotografia do rosto',
  OTHER: 'Documento adicional',
};

const contactCategoryLabels: Record<string, string> = {
  VOUCHER_PURCHASE: 'Compra de voucher',
  PASSWORD_CHANGED: 'Password alterada',
  PIN_CHANGED: 'PIN alterado',
  KYC_APPROVED: 'KYC aprovado',
  KYC_REJECTED: 'KYC rejeitado',
};

function DetailEmpty({ icon: Icon, title, description }: { icon: typeof Mail; title: string; description: string }) {
  return <div className="detail-empty"><span><Icon size={25} /></span><h3>{title}</h3><p>{description}</p></div>;
}

export function CustomerDetailPage() {
  const { pathname } = useLocation();
  const id = decodeURIComponent(pathname.split('/')[2] ?? '');
  const navigate = useNavigate();
  const { admin } = useAuth();
  const [customer, setCustomer] = useState<CustomerDetail | null>(null);
  const [error, setError] = useState('');
  const [decisionTarget, setDecisionTarget] = useState<DecisionTarget | null>(null);
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [visibilityBusyId, setVisibilityBusyId] = useState<string | null>(null);
  const [blockOpen, setBlockOpen] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [tab, setTab] = useState<DetailTab>('overview');
  const canReview = ['SUPER_ADMIN', 'COMPLIANCE'].includes(admin?.role ?? '');
  const canEdit = ['SUPER_ADMIN', 'OPERATIONS', 'COMPLIANCE'].includes(admin?.role ?? '');
  const canSeeVouchers = ['SUPER_ADMIN', 'OPERATIONS'].includes(admin?.role ?? '');

  const load = () =>
    api<CustomerDetail>(`/customers/${encodeURIComponent(id)}`)
      .then(setCustomer)
      .catch(() => setError('Não foi possível carregar este cliente.'));

  useEffect(() => {
    void load();
  }, [id]);

  async function submitDecision(event: FormEvent) {
    event.preventDefault();
    if (!decisionTarget) return;
    setBusy(true);
    setError('');
    try {
      const path = decisionTarget.documentId
        ? `/customers/${encodeURIComponent(id)}/documents/${encodeURIComponent(
            decisionTarget.documentId,
          )}/kyc-decisions`
        : `/customers/${encodeURIComponent(id)}/kyc-decisions`;
      await api(path, {
        method: 'POST',
        body: JSON.stringify({
          decision: decisionTarget.decision,
          ...(reasonCode ? { reasonCode } : {}),
          ...(note ? { note } : {}),
        }),
      });
      setDecisionTarget(null);
      setReasonCode('');
      setNote('');
      await load();
    } catch {
      setError('A decisão não foi guardada. Confirme os dados e tente novamente.');
    } finally {
      setBusy(false);
    }
  }

  async function toggleVisibility(document: Document) {
    setVisibilityBusyId(document.id);
    setError('');
    try {
      await api(
        `/customers/${encodeURIComponent(id)}/documents/${encodeURIComponent(document.id)}/visibility`,
        { method: 'PATCH', body: JSON.stringify({ visible: !document.visible }) },
      );
      await load();
    } catch {
      setError('Não foi possível atualizar a visibilidade do documento.');
    } finally {
      setVisibilityBusyId(null);
    }
  }

  async function unblockAccount() {
    setStatusBusy(true);
    setError('');
    try {
      await api(`/customers/${encodeURIComponent(id)}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ active: true }),
      });
      await load();
    } catch {
      setError('Não foi possível desbloquear a conta.');
    } finally {
      setStatusBusy(false);
    }
  }

  if (!customer && !error) return <Loading label="A carregar o processo do cliente" />;
  if (!customer) {
    return (
      <div className="empty-state">
        <p>{error}</p>
        <button className="button button--secondary" onClick={() => navigate(-1)}>
          Voltar
        </button>
      </div>
    );
  }

  const canDecideAccount =
    canReview &&
    ['PENDING', 'IN_REVIEW', 'MORE_INFORMATION_REQUIRED'].includes(customer.kycStatus);

  return (
    <div className="page customer-detail">
      <Link className="back-link" to="/customers">
        <ArrowLeft size={17} /> Voltar aos clientes
      </Link>

      <div className="customer-hero">
        <div className="avatar avatar--large">
          {customer.fullName.trim().split(/\s+/).filter((_, index, names) => index === 0 || index === names.length - 1).map(name => name[0]).join('').slice(0, 2).toUpperCase()}
        </div>
        <div className="customer-hero__identity">
          <div>
            <h1>{customer.fullName}</h1>
          </div>
          <div className="customer-state-list">
            <span>KYC <StatusBadge value={customer.kycStatus} /></span>
            <span>Risco <StatusBadge value={customer.riskLevel} /></span>
            <span>Conta <StatusBadge value={customer.active ? 'ACTIVE' : 'BLOCKED'} /></span>
          </div>
          <p>
            ID {customer.id}
            {customer.identitySubject && <> · Identity {customer.identitySubject}</>}
          </p>
        </div>
        {canReview && (
          <div className="customer-hero__actions">
            {canDecideAccount && (
              <>
                <button
                  className="button button--secondary"
                  onClick={() => setDecisionTarget({ decision: 'MORE_INFORMATION_REQUIRED' })}
                >
                  <MessageSquareText size={17} /> Pedir informação
                </button>
                <button
                  className="button button--danger-soft"
                  onClick={() => setDecisionTarget({ decision: 'REJECTED' })}
                >
                  <X size={17} /> Rejeitar
                </button>
                <button
                  className="button button--primary"
                  onClick={() => setDecisionTarget({ decision: 'APPROVED' })}
                >
                  <Check size={17} /> Aprovar conta
                </button>
              </>
            )}
            {customer.active ? (
              <button
                className="button button--danger-soft"
                onClick={() => setBlockOpen(true)}
              >
                <Ban size={17} /> Bloquear conta
              </button>
            ) : (
              <button
                className="button button--secondary"
                onClick={unblockAccount}
                disabled={statusBusy}
              >
                <Check size={17} /> {statusBusy ? 'A desbloquear…' : 'Desbloquear conta'}
              </button>
            )}
          </div>
        )}
      </div>

      {!customer.active && (
        <div className="customer-block-notice" role="status">
          <ShieldAlert size={21} />
          <div><strong>Conta bloqueada</strong><p>{customer.blockedReason || 'Sem motivo indicado.'}</p></div>
          {customer.blockedAt && <time>{dateTime(customer.blockedAt)}</time>}
        </div>
      )}

      {error && <div className="form-error">{error}</div>}

      <div className="account-tabs customer-detail-tabs" aria-label="Secções do cliente">
        <button
          className={tab === 'overview' ? 'account-tab account-tab--active' : 'account-tab'}
          aria-pressed={tab === 'overview'}
          onClick={() => setTab('overview')}
        >
          <CircleUserRound size={16} /> Visão geral
        </button>
        <button
          className={tab === 'documents' ? 'account-tab account-tab--active' : 'account-tab'}
          aria-pressed={tab === 'documents'}
          onClick={() => setTab('documents')}
        >
          <FileImage size={16} /> Documentos KYC <span className="detail-tab-count">{customer.documents.length}</span>
        </button>
        <button
          className={tab === 'decisions' ? 'account-tab account-tab--active' : 'account-tab'}
          aria-pressed={tab === 'decisions'}
          onClick={() => setTab('decisions')}
        >
          <History size={16} /> Histórico de decisões <span className="detail-tab-count">{customer.reviews.length}</span>
        </button>
        <button
          className={tab === 'movements' ? 'account-tab account-tab--active' : 'account-tab'}
          aria-pressed={tab === 'movements'}
          onClick={() => setTab('movements')}
        >
          <ArrowLeftRight size={16} /> Movimentos
        </button>
        {canSeeVouchers && (
          <button
            className={tab === 'vouchers' ? 'account-tab account-tab--active' : 'account-tab'}
            aria-pressed={tab === 'vouchers'}
          onClick={() => setTab('vouchers')}
          >
            <Ticket size={16} /> Vouchers
          </button>
        )}
        <button
          className={tab === 'contacts' ? 'account-tab account-tab--active' : 'account-tab'}
          aria-pressed={tab === 'contacts'}
          onClick={() => setTab('contacts')}
        >
          <Mail size={16} /> Contactos
        </button>
      </div>

      {tab === 'overview' && (
        <div className="detail-grid">
          <div className="detail-main">
            <section className="panel">
              <div className="panel__heading">
                <div>
                  <h2>Dados do cliente</h2>
                  <p>Informação fornecida durante o registo.</p>
                </div>
                {canEdit ? (
                  <button
                    type="button"
                    className="button button--secondary"
                    onClick={() => setEditOpen(true)}
                  >
                    <Pencil size={15} /> Editar
                  </button>
                ) : (
                  <CircleUserRound size={21} />
                )}
              </div>
              <div className="info-grid">
                <Info icon={CircleUserRound} label="Nome legal" value={customer.legalName} />
                <Info icon={Calendar} label="Data de nascimento" value={customer.dateOfBirth} />
                <Info icon={Mail} label="Email" value={customer.email} />
                <Info icon={Phone} label="Telefone" value={customer.phone} />
                <Info icon={MapPin} label="Morada" value={customer.residenceAddress} wide />
              </div>
            </section>
            {customer.riskAssessment && (
              <section className="panel customer-risk-panel">
                <div className="panel__heading"><div><h2>Avaliação automática de risco</h2><p>Verificação de consistência dos dados e documentos.</p></div><StatusBadge value={customer.riskAssessment.level} /></div>
                <div className="customer-risk-body">
                  <div className="customer-risk-score"><ShieldAlert size={22} /><strong>{customer.riskAssessment.score}<small> / 100 pontos</small></strong><span>{dateTime(customer.riskAssessment.evaluatedAt)}</span></div>
                  {customer.riskAssessment.reasons.length ? <ul>{customer.riskAssessment.reasons.map(reason => <li key={reason.code}><span>{reason.description}</span><strong>+{reason.points}</strong></li>)}</ul> : <p>Não foram detetadas inconsistências nas regras verificadas.</p>}
                  {customer.riskAssessment.missing.length > 0 && <p className="customer-risk-missing"><strong>Avaliação incompleta:</strong> falta preencher {customer.riskAssessment.missing.join(', ').toLowerCase()}.</p>}
                  <p className="customer-risk-caption">Triagem automática, não uma confirmação de fraude. A pontuação não aprova nem bloqueia a conta. Baixo: 0–19 · Médio: 20–59 · Alto: 60–100. Sem sinais e com dados em falta: por avaliar.</p>
                </div>
              </section>
            )}
          </div>

          <aside className="detail-side">
            <section className="panel status-card">
              <div className="panel__heading">
                <div><h2>Contas e cartões</h2><p>Contas associadas e cartões emitidos.</p></div>
                <CreditCard size={20} />
              </div>
              {customer.accounts.length === 0 && <div className="empty-inline">Sem contas associadas.</div>}
              {customer.accounts.map((account) => (
                <div className="account-card" key={account.id}>
                  <div>
                    <span>{account.name}</span>
                    <StatusBadge value={account.status} />
                  </div>
                  <strong>{account.ibanMasked}</strong>
                  <small>{account.currency} · {account.isDefault ? 'Principal' : 'Secundária'}</small>
                </div>
              ))}
              <CardsSection customerId={customer.id} canManage={canEdit} />
            </section>

            <section className="panel status-card">
              <div className="panel__heading">
                <h2>Eupago</h2>
                <Landmark size={20} />
              </div>
              <div className="status-card__row">
                <span>Estado</span>
                <StatusBadge value={customer.partner.status} />
              </div>
              <div className="status-card__row">
                <span>Última sincronização</span>
                <strong>{dateTime(customer.partner.lastSyncedAt)}</strong>
              </div>
              {customer.partner.balance && (
                <div className="balance-block">
                  <span>Saldo reportado</span>
                  <strong>
                    {money(
                      customer.partner.balance.amountMinor,
                      customer.partner.balance.currency,
                    )}
                  </strong>
                  <small>{dateTime(customer.partner.balance.asOf)}</small>
                </div>
              )}
              <button className="button button--secondary button--full" disabled>
                <RefreshCw size={16} /> Sincronizar
              </button>
            </section>
          </aside>
        </div>
      )}

      {tab === 'documents' && (
        <section className="panel">
          <div className="panel__heading">
            <div>
              <h2>Documentos KYC</h2>
              <p>O acesso a cada documento fica registado na auditoria.</p>
            </div>
            {canReview ? (
              <button
                type="button"
                className="button button--secondary"
                onClick={() => setUploadOpen(true)}
              >
                <Upload size={15} /> Adicionar documento
              </button>
            ) : (
              <FileImage size={21} />
            )}
          </div>
          {customer.documents.length === 0 ? (
            <DetailEmpty icon={FileImage} title="Sem documentos associados" description="Os documentos de identificação deste cliente serão apresentados aqui." />
          ) : (
            <div className="documents-grid">
              {customer.documents.map((document) => (
                <article className="document-card" key={document.id}>
                  <div className="document-card__preview">
                    <FileImage size={30} />
                    <span>Pré-visualização protegida</span>
                  </div>
                  <div className="document-card__body">
                    <strong>{documentKindLabels[document.kind] ?? document.kind}</strong>
                    <div className="document-card__badges">
                      <StatusBadge value={document.status} />
                      {canReview && (
                        <button
                          type="button"
                          className={`button-tiny ${document.visible ? '' : 'button-tiny--danger'}`}
                          onClick={() => toggleVisibility(document)}
                          disabled={visibilityBusyId === document.id}
                          title={
                            document.visible
                              ? 'Visível para o cliente na app — clique para ocultar'
                              : 'Oculto para o cliente na app — clique para mostrar'
                          }
                        >
                          {document.visible ? <Eye size={12} /> : <EyeOff size={12} />}
                          {document.visible ? 'Visível' : 'Oculto'}
                        </button>
                      )}
                    </div>
                    <small>{dateTime(document.uploadedAt)}</small>
                    {canReview && document.available ? (
                      <a
                        className="document-link"
                        href={`/api/customers/${encodeURIComponent(
                          customer.id,
                        )}/documents/${encodeURIComponent(document.id)}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Abrir documento <ExternalLink size={14} />
                      </a>
                    ) : (
                      <span className="document-unavailable">
                        {canReview
                          ? 'Disponível apenas em modo live'
                          : 'Acesso reservado a Compliance'}
                      </span>
                    )}
                    {canReview && (
                      <div className="document-card__actions">
                        <button
                          type="button"
                          className="button-tiny"
                          onClick={() =>
                            setDecisionTarget({ decision: 'APPROVED', documentId: document.id })
                          }
                        >
                          Aprovar
                        </button>
                        <button
                          type="button"
                          className="button-tiny button-tiny--danger"
                          onClick={() =>
                            setDecisionTarget({ decision: 'REJECTED', documentId: document.id })
                          }
                        >
                          Rejeitar
                        </button>
                        <button
                          type="button"
                          className="button-tiny"
                          onClick={() =>
                            setDecisionTarget({
                              decision: 'MORE_INFORMATION_REQUIRED',
                              documentId: document.id,
                            })
                          }
                        >
                          Pedir info
                        </button>
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      )}

      {tab === 'decisions' && (
        <section className="panel">
          <div className="panel__heading">
            <div>
              <h2>Histórico de decisões</h2>
              <p>Registo imutável das revisões deste processo.</p>
            </div>
            <ShieldAlert size={21} />
          </div>
          {customer.reviews.length === 0 ? (
            <DetailEmpty icon={History} title="Ainda sem decisões" description="As revisões deste processo ficam registadas aqui, com a data e o responsável." />
          ) : (
            <div className="timeline">
              {customer.reviews.map((review) => (
                <div className="timeline__item" key={review.id}>
                  <span className="timeline__marker" />
                  <div>
                    <div>
                      <StatusBadge value={review.decision} />
                      <time>{dateTime(review.decidedAt)}</time>
                    </div>
                    <strong>{review.decidedBy.fullName}</strong>
                    {review.note && <p>{review.note}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {tab === 'movements' && (
        <MovementsPanel customerId={customer.id} accounts={customer.accounts} />
      )}

      {tab === 'vouchers' && canSeeVouchers && (
        <CustomerVouchersTab customerId={customer.id} />
      )}

      {tab === 'contacts' && <CustomerContactsTab customerId={customer.id} />}

      {decisionTarget && (
        <div className="modal-backdrop" role="presentation">
          <form className="modal" onSubmit={submitDecision}>
            <div className="modal__heading">
              <div>
                <span className="eyebrow">
                  {decisionTarget.documentId ? 'Decisão sobre documento' : 'Decisão KYC'}
                </span>
                <h2>
                  {decisionTarget.decision === 'APPROVED'
                    ? decisionTarget.documentId
                      ? 'Aprovar este documento?'
                      : 'Aprovar esta conta?'
                    : decisionTarget.decision === 'REJECTED'
                      ? decisionTarget.documentId
                        ? 'Rejeitar este documento?'
                        : 'Rejeitar esta conta?'
                      : 'Pedir mais informação?'}
                </h2>
              </div>
              <button
                className="icon-button"
                type="button"
                onClick={() => setDecisionTarget(null)}
                aria-label="Fechar"
              >
                <X size={20} />
              </button>
            </div>
            <p>
              Esta ação será enviada à QUI API e registada com o seu utilizador.
            </p>
            {decisionTarget.decision !== 'APPROVED' && (
              <label>
                Motivo
                <select
                  value={reasonCode}
                  onChange={(event) => setReasonCode(event.target.value)}
                  required
                >
                  <option value="">Selecionar motivo</option>
                  <option value="DOCUMENT_UNREADABLE">Documento ilegível</option>
                  <option value="DATA_MISMATCH">Dados não correspondem</option>
                  <option value="SELFIE_MISMATCH">Fotografia não corresponde</option>
                  <option value="DOCUMENT_EXPIRED">Documento expirado</option>
                  <option value="OTHER">Outro</option>
                </select>
              </label>
            )}
            <label>
              Nota {decisionTarget.decision === 'APPROVED' && '(opcional)'}
              <textarea
                value={note}
                onChange={(event) => setNote(event.target.value)}
                required={decisionTarget.decision !== 'APPROVED'}
                minLength={decisionTarget.decision !== 'APPROVED' ? 3 : undefined}
                maxLength={1000}
                rows={4}
                placeholder="Contexto da decisão..."
              />
            </label>
            <div className="modal__actions">
              <button
                type="button"
                className="button button--secondary"
                onClick={() => setDecisionTarget(null)}
              >
                Cancelar
              </button>
              <button
                className={decisionTarget.decision === 'REJECTED' ? 'button button--danger' : 'button button--primary'}
                disabled={busy}
              >
                {busy ? <span className="spinner spinner--button" /> : 'Confirmar decisão'}
              </button>
            </div>
          </form>
        </div>
      )}

      {editOpen && (
        <EditCustomerModal
          customer={customer}
          onClose={() => setEditOpen(false)}
          onSaved={async () => {
            setEditOpen(false);
            await load();
          }}
        />
      )}

      {uploadOpen && (
        <UploadDocumentModal
          customerId={customer.id}
          onClose={() => setUploadOpen(false)}
          onSaved={async () => {
            setUploadOpen(false);
            await load();
          }}
        />
      )}

      {blockOpen && (
        <BlockAccountModal
          customerId={customer.id}
          onClose={() => setBlockOpen(false)}
          onBlocked={async () => {
            setBlockOpen(false);
            await load();
          }}
        />
      )}
    </div>
  );
}

const TRANSACTIONS_PAGE_SIZE = 20;

function MovementsPanel({
  customerId,
  accounts,
}: {
  customerId: string;
  accounts: CustomerDetail['accounts'];
}) {
  const [accountId, setAccountId] = useState('');
  const [items, setItems] = useState<Transaction[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    const query = new URLSearchParams({
      page: String(page),
      pageSize: String(TRANSACTIONS_PAGE_SIZE),
      ...(accountId ? { accountId } : {}),
    });
    api<TransactionPage>(
      `/customers/${encodeURIComponent(customerId)}/transactions?${query}`,
    )
      .then((result) => {
        setItems(result.items);
        setTotal(result.total);
      })
      .catch(() => setError('Não foi possível carregar os movimentos.'))
      .finally(() => setLoading(false));
  }, [customerId, accountId, page]);

  function changeAccount(nextAccountId: string) {
    setAccountId(nextAccountId);
    setPage(1);
  }

  return (
    <section className="panel">
      <div className="panel__heading">
        <div>
          <h2>Movimentos</h2>
          <p>Extrato das contas do cliente.</p>
        </div>
        {accounts.length > 1 ? (
          <div className="select-field">
            <select
              value={accountId}
              onChange={(event) => changeAccount(event.target.value)}
              aria-label="Filtrar por conta"
            >
              <option value="">Todas as contas</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name ?? account.currency}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <ArrowLeftRight size={21} />
        )}
      </div>

      {error && <div className="form-error">{error}</div>}

      {loading ? (
        <Loading label="A carregar movimentos" />
      ) : items.length === 0 ? (
        <DetailEmpty icon={ArrowLeftRight} title="Sem movimentos" description="Os movimentos das contas selecionadas serão apresentados aqui." />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Descrição</th>
                <th>Tipo</th>
                <th>Estado</th>
                <th className="detail-numeric">Valor</th>
                <th className="detail-numeric">Saldo após</th>
              </tr>
            </thead>
            <tbody>
              {items.map((transaction) => (
                <tr key={transaction.id}>
                  <td>{dateTime(transaction.createdAt)}</td>
                  <td>
                    <strong>{transaction.title}</strong>
                    {transaction.reference && <small className="table-subtitle">Ref. {transaction.reference}</small>}
                  </td>
                  <td><span className="detail-type">{transaction.type}</span></td>
                  <td><StatusBadge value={transaction.status} /></td>
                  <td className="detail-numeric">
                    <span className={transaction.amountMinor < 0 ? 'amount amount--debit' : 'amount amount--credit'}>
                      {money(transaction.amountMinor, transaction.currency)}
                    </span>
                  </td>
                  <td className="detail-numeric">
                    {transaction.balanceAfterMinor == null
                      ? '—'
                      : money(transaction.balanceAfterMinor, transaction.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pagination
        page={page}
        pageSize={TRANSACTIONS_PAGE_SIZE}
        total={total}
        onPageChange={setPage}
      />
    </section>
  );
}

const CUSTOMER_PURCHASES_PAGE_SIZE = 10;

function CustomerVouchersTab({ customerId }: { customerId: string }) {
  const [data, setData] = useState<VoucherPurchasePage | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [responseTarget, setResponseTarget] = useState<VoucherPurchase | null>(null);

  useEffect(() => {
    setLoading(true);
    setError('');
    const query = new URLSearchParams({
      userId: customerId,
      page: String(page),
      pageSize: String(CUSTOMER_PURCHASES_PAGE_SIZE),
    });
    api<VoucherPurchasePage>(`/vouchers/purchases?${query}`)
      .then(setData)
      .catch(() => setError('Não foi possível carregar o histórico de vouchers.'))
      .finally(() => setLoading(false));
  }, [customerId, page]);

  return (
    <div className="detail-main">
      <section className="panel">
        <div className="panel__heading">
          <div>
            <h2>Histórico de compras de vouchers</h2>
            <p>Vouchers comprados por este cliente através da app.</p>
          </div>
        </div>
        {error && <div className="form-error">{error}</div>}
        {loading ? (
          <Loading />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Voucher</th>
                  <th>Quantidade</th>
                  <th>Valor pago</th>
                  <th>Estado</th>
                  <th>Data</th>
                  <th aria-label="Ações" />
                </tr>
              </thead>
              <tbody>
                {data?.items.map((purchase) => (
                  <tr key={purchase.id}>
                    <td>
                      {purchase.voucherName ?? '—'}
                      {purchase.productOptionCode && (
                        <div className="table-subtitle"><code>{purchase.productOptionCode}</code></div>
                      )}
                    </td>
                    <td>{purchase.quantity}</td>
                    <td>{new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(purchase.amount)}</td>
                    <td>
                      <StatusBadge value={purchase.status} />
                      {purchase.errorMessage && (
                        <div className="table-subtitle">{purchase.errorMessage}</div>
                      )}
                    </td>
                    <td>{dateTime(purchase.createdAt)}</td>
                    <td>
                      {purchase.responsePayload != null && (
                        <button
                          type="button"
                          className="table-action"
                          onClick={() => setResponseTarget(purchase)}
                        >
                          Ver resposta
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {(data?.items.length ?? 0) === 0 && (
                  <tr>
                    <td colSpan={6}><DetailEmpty icon={Ticket} title="Ainda sem compras de vouchers" description="Consulta aqui os vouchers comprados por este cliente e o estado de cada compra." /></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {data && (
          <Pagination
            page={page}
            pageSize={CUSTOMER_PURCHASES_PAGE_SIZE}
            total={data.total}
            onPageChange={setPage}
          />
        )}
      </section>

      {responseTarget && (
        <div className="modal-backdrop">
          <div className="modal">
            <div className="modal__heading">
              <div>
                <span className="eyebrow">Resposta Pagaqui</span>
                <h2>{responseTarget.voucherName ?? 'Voucher'}</h2>
              </div>
              <button className="icon-button" type="button" onClick={() => setResponseTarget(null)}>
                <X size={20} />
              </button>
            </div>
            <pre className="json-viewer">
              {JSON.stringify(responseTarget.responsePayload, null, 2)}
            </pre>
            <div className="modal__actions">
              <button
                type="button"
                className="button button--secondary"
                onClick={() => setResponseTarget(null)}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const CUSTOMER_CONTACTS_PAGE_SIZE = 20;

function CustomerContactsTab({ customerId }: { customerId: string }) {
  const [data, setData] = useState<CustomerContactPage | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    const query = new URLSearchParams({
      page: String(page),
      pageSize: String(CUSTOMER_CONTACTS_PAGE_SIZE),
    });
    api<CustomerContactPage>(`/customers/${encodeURIComponent(customerId)}/contacts?${query}`)
      .then(setData)
      .catch(() => setError('Não foi possível carregar o histórico de contactos.'))
      .finally(() => setLoading(false));
  }, [customerId, page]);

  return (
    <div className="detail-main">
      <section className="panel">
        <div className="panel__heading">
          <div>
            <h2>Histórico de contactos</h2>
            <p>Emails enviados a este cliente, com o respetivo estado de envio.</p>
          </div>
          <Mail size={20} />
        </div>
        {error && <div className="form-error">{error}</div>}
        {loading ? (
          <Loading />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Destinatário</th>
                  <th>Assunto</th>
                  <th>Estado</th>
                  <th>Data</th>
                </tr>
              </thead>
              <tbody>
                {data?.items.map((contact: CustomerContact) => (
                  <tr key={contact.id}>
                    <td>{contactCategoryLabels[contact.category] ?? contact.category}</td>
                    <td>{contact.recipient}</td>
                    <td>{contact.subject}</td>
                    <td>
                      <StatusBadge value={contact.status} />
                      {contact.errorMessage && (
                        <div className="table-subtitle">{contact.errorMessage}</div>
                      )}
                    </td>
                    <td>{dateTime(contact.createdAt)}</td>
                  </tr>
                ))}
                {(data?.items.length ?? 0) === 0 && (
                  <tr>
                    <td colSpan={5}><DetailEmpty icon={Mail} title="Ainda sem contactos" description="Os emails enviados a este cliente (compras, alertas de conta, etc.) ficam registados aqui." /></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {data && (
          <Pagination
            page={page}
            pageSize={CUSTOMER_CONTACTS_PAGE_SIZE}
            total={data.total}
            onPageChange={setPage}
          />
        )}
      </section>
    </div>
  );
}

function CardsSection({
  customerId,
  canManage,
}: {
  customerId: string;
  canManage: boolean;
}) {
  const [cards, setCards] = useState<Card[] | null>(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<Card | null>(null);

  const load = () =>
    api<{ items: Card[] }>(`/customers/${encodeURIComponent(customerId)}/cards`)
      .then((result) => setCards(result.items))
      .catch(() => setError('Não foi possível carregar os cartões.'));

  useEffect(() => {
    void load();
  }, [customerId]);

  async function toggleFrozen(card: Card) {
    setBusyId(card.id);
    setError('');
    try {
      await api(
        `/customers/${encodeURIComponent(customerId)}/cards/${encodeURIComponent(card.id)}/freeze`,
        { method: 'PATCH', body: JSON.stringify({ frozen: !card.isFrozen }) },
      );
      await load();
    } catch {
      setError('Não foi possível atualizar o cartão.');
    } finally {
      setBusyId(null);
    }
  }

  async function confirmCancel() {
    if (!cancelTarget) return;
    setBusyId(cancelTarget.id);
    setError('');
    try {
      await api(
        `/customers/${encodeURIComponent(customerId)}/cards/${encodeURIComponent(cancelTarget.id)}/cancel`,
        { method: 'POST' },
      );
      setCancelTarget(null);
      await load();
    } catch {
      setError('Não foi possível cancelar o cartão.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="cards-section">
      <span className="cards-section__heading">Cartões</span>
      {error && <div className="form-error">{error}</div>}
      {!cards ? (
        <div className="empty-inline">A carregar cartões…</div>
      ) : cards.length === 0 ? (
        <div className="empty-inline">Sem cartões associados.</div>
      ) : (
        cards.map((card) => (
          <div className="card-row" key={card.id}>
            <div className="card-row__info">
              <strong>
                {card.nickname || (card.type === 'PHYSICAL' ? 'Cartão físico' : 'Cartão virtual')}
              </strong>
              <small>{card.brand ?? 'Cartão'} •••• {card.last4 ?? '----'}</small>
              <div className="card-row__badges">
                <StatusBadge value={card.type ?? 'VIRTUAL'} />
                {!card.isActive ? (
                  <StatusBadge value="CANCELLED" />
                ) : card.isFrozen ? (
                  <StatusBadge value="FROZEN" />
                ) : (
                  <StatusBadge value="ACTIVE" />
                )}
              </div>
            </div>
            {canManage && card.isActive && (
              <div className="card-row__actions">
                <button
                  type="button"
                  className="button-tiny"
                  onClick={() => toggleFrozen(card)}
                  disabled={busyId === card.id}
                >
                  <Snowflake size={12} /> {card.isFrozen ? 'Descongelar' : 'Congelar'}
                </button>
                <button
                  type="button"
                  className="button-tiny button-tiny--danger"
                  onClick={() => setCancelTarget(card)}
                  disabled={busyId === card.id}
                >
                  <Ban size={12} /> Cancelar
                </button>
              </div>
            )}
          </div>
        ))
      )}

      {cancelTarget && (
        <div className="modal-backdrop" role="presentation">
          <div className="modal">
            <div className="modal__heading">
              <div>
                <span className="eyebrow">Cartões</span>
                <h2>Cancelar cartão?</h2>
              </div>
              <button
                className="icon-button"
                type="button"
                onClick={() => setCancelTarget(null)}
                aria-label="Fechar"
              >
                <X size={20} />
              </button>
            </div>
            <p>
              Esta ação é irreversível. O cartão {cancelTarget.brand ?? ''} ••••{' '}
              {cancelTarget.last4 ?? ''} deixará de poder ser usado.
            </p>
            <div className="modal__actions">
              <button
                type="button"
                className="button button--secondary"
                onClick={() => setCancelTarget(null)}
              >
                Voltar
              </button>
              <button
                type="button"
                className="button button--danger"
                onClick={confirmCancel}
                disabled={busyId === cancelTarget.id}
              >
                {busyId === cancelTarget.id ? 'A cancelar…' : 'Cancelar cartão'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EditCustomerModal({
  customer,
  onClose,
  onSaved,
}: {
  customer: CustomerDetail;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [fullName, setFullName] = useState(customer.fullName);
  const [email, setEmail] = useState(customer.email ?? '');
  const [legalName, setLegalName] = useState(customer.legalName ?? '');
  const [dateOfBirth, setDateOfBirth] = useState(customer.dateOfBirth ?? '');
  const [country, setCountry] = useState(customer.country ?? '');
  const [residenceAddress, setResidenceAddress] = useState(customer.residenceAddress ?? '');
  const [taxId, setTaxId] = useState(customer.taxId ?? '');
  const [preferredLocale, setPreferredLocale] = useState(customer.preferredLocale || 'pt');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    const input: UpdateCustomerInput = {
      fullName,
      email,
      preferredLocale,
      ...(legalName.trim() ? { legalName: legalName.trim() } : {}),
      ...(dateOfBirth ? { dateOfBirth } : {}),
      ...(country.trim() ? { countryOfResidence: country.trim() } : {}),
      ...(residenceAddress.trim() ? { residenceAddress: residenceAddress.trim() } : {}),
      ...(taxId.trim() ? { taxId: taxId.trim() } : {}),
    };
    try {
      await api(`/customers/${encodeURIComponent(customer.id)}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      });
      await onSaved();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 409
          ? 'Já existe um cliente com este email.'
          : 'Não foi possível guardar as alterações.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <form className="modal" onSubmit={submit}>
        <div className="modal__heading">
          <div>
            <span className="eyebrow">Dados do cliente</span>
            <h2>Editar dados</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Fechar">
            <X size={20} />
          </button>
        </div>
        <label>
          Nome completo
          <input
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            required
            minLength={2}
            maxLength={120}
          />
        </label>
        <label>
          Email
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </label>
        <label>
          Nome legal
          <input
            value={legalName}
            onChange={(event) => setLegalName(event.target.value)}
            maxLength={120}
          />
        </label>
        <label>
          Data de nascimento
          <input
            type="date"
            value={dateOfBirth}
            onChange={(event) => setDateOfBirth(event.target.value)}
          />
        </label>
        <label>
          País de residência
          <input
            value={country}
            onChange={(event) => setCountry(event.target.value)}
            maxLength={100}
            placeholder="PT"
          />
        </label>
        <label>
          Morada
          <input
            value={residenceAddress}
            onChange={(event) => setResidenceAddress(event.target.value)}
            maxLength={300}
          />
        </label>
        <label>
          NIF
          <input
            value={taxId}
            onChange={(event) => setTaxId(event.target.value)}
            maxLength={50}
          />
        </label>
        <label>
          Idioma preferido
          <select
            value={preferredLocale}
            onChange={(event) => setPreferredLocale(event.target.value)}
          >
            <option value="pt">Português</option>
            <option value="es">Español</option>
            <option value="en">English</option>
          </select>
        </label>
        {error && <div className="form-error">{error}</div>}
        <div className="modal__actions">
          <button type="button" className="button button--secondary" onClick={onClose}>
            Cancelar
          </button>
          <button className="button button--primary" disabled={busy}>
            {busy ? 'A guardar…' : 'Guardar alterações'}
          </button>
        </div>
      </form>
    </div>
  );
}

function UploadDocumentModal({
  customerId,
  onClose,
  onSaved,
}: {
  customerId: string;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [kind, setKind] = useState<'ID_FRONT' | 'ID_BACK' | 'SELFIE' | 'OTHER'>('OTHER');
  const [visible, setVisible] = useState(true);
  const [fileName, setFileName] = useState('');
  const [dataUrl, setDataUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  function changeKind(nextKind: typeof kind) {
    setKind(nextKind);
    setVisible(nextKind !== 'SELFIE');
  }

  function handleFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    setError('');
    if (!file) {
      setFileName('');
      setDataUrl('');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('O ficheiro tem de ser uma imagem.');
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      setError('O ficheiro não pode exceder 12MB.');
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setDataUrl(String(reader.result ?? ''));
    reader.readAsDataURL(file);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!dataUrl) {
      setError('Selecione um ficheiro para enviar.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await api(`/customers/${encodeURIComponent(customerId)}/documents`, {
        method: 'POST',
        body: JSON.stringify({ kind, dataUrl, visible }),
      });
      await onSaved();
    } catch {
      setError('Não foi possível enviar o documento.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <form className="modal" onSubmit={submit}>
        <div className="modal__heading">
          <div>
            <span className="eyebrow">Documentos KYC</span>
            <h2>Adicionar documento</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Fechar">
            <X size={20} />
          </button>
        </div>
        <label>
          Tipo de documento
          <select value={kind} onChange={(event) => changeKind(event.target.value as typeof kind)}>
            <option value="ID_FRONT">Cartão de cidadão — frente</option>
            <option value="ID_BACK">Cartão de cidadão — verso</option>
            <option value="SELFIE">Fotografia do rosto</option>
            <option value="OTHER">Documento adicional</option>
          </select>
        </label>
        <label>
          Ficheiro
          <input type="file" accept="image/*" onChange={handleFile} required />
        </label>
        {fileName && <p className="upload-filename">{fileName}</p>}
        <label className="checkbox-field">
          <input
            type="checkbox"
            checked={visible}
            onChange={(event) => setVisible(event.target.checked)}
          />
          Visível para o cliente na app
        </label>
        {error && <div className="form-error">{error}</div>}
        <div className="modal__actions">
          <button type="button" className="button button--secondary" onClick={onClose}>
            Cancelar
          </button>
          <button className="button button--primary" disabled={busy}>
            {busy ? 'A enviar…' : 'Adicionar documento'}
          </button>
        </div>
      </form>
    </div>
  );
}

function BlockAccountModal({
  customerId,
  onClose,
  onBlocked,
}: {
  customerId: string;
  onClose: () => void;
  onBlocked: () => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api(`/customers/${encodeURIComponent(customerId)}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ active: false, reason: reason.trim() }),
      });
      await onBlocked();
    } catch {
      setError('Não foi possível bloquear a conta.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <form className="modal" onSubmit={submit}>
        <div className="modal__heading">
          <div>
            <span className="eyebrow">Segurança</span>
            <h2>Bloquear conta?</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Fechar">
            <X size={20} />
          </button>
        </div>
        <p>
          O cliente deixa de conseguir iniciar sessão ou movimentar a conta, mesmo já estando
          aprovada. Esta ação fica registada na auditoria.
        </p>
        <label>
          Motivo do bloqueio
          <textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            required
            minLength={3}
            maxLength={500}
            rows={4}
            placeholder="Ex.: suspeita de fraude, pedido de compliance..."
          />
        </label>
        {error && <div className="form-error">{error}</div>}
        <div className="modal__actions">
          <button type="button" className="button button--secondary" onClick={onClose}>
            Cancelar
          </button>
          <button className="button button--danger" disabled={busy}>
            {busy ? 'A bloquear…' : 'Bloquear conta'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Info({
  icon: Icon,
  label,
  value,
  wide,
}: {
  icon: typeof Mail;
  label: string;
  value?: string | null;
  wide?: boolean;
}) {
  return (
    <div className={`info-item ${wide ? 'info-item--wide' : ''}`}>
      <Icon size={17} />
      <div>
        <span>{label}</span>
        <strong>{value || '—'}</strong>
      </div>
    </div>
  );
}
